import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Modal, Button, ButtonGroup, pxToRem } from '@modernhealth/vital-components';
import { filterNullish } from '@modernhealth/filter-nullish';

import { Dispatch } from 'src/config/rootStore';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { getUserTimeZone } from 'src/modules/user/selectors/userSelectors';
import { useAllTimezonesQuery } from 'src/modules/user/documents/allTimeZonesQuery.generated';

export const TIME_ZONE_MODAL_STORAGE_KEY = 'TIME_ZONE_DIFF_PROMPT_LAST_CANCELLATION_v1';

const StyledButtonGroup = styled(ButtonGroup)`
    margin-top: ${pxToRem(20)};
`;

interface Props {
    getDeviceTimeZone?: () => string;
}

export const TimeZoneModal: React.FC<Props> = ({
    getDeviceTimeZone = () => new Intl.DateTimeFormat().resolvedOptions().timeZone,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<Dispatch>();
    const localTimezone = useSelector(getUserTimeZone);
    const [visible, setVisible] = useState(false);
    const { data, loading } = useAllTimezonesQuery();

    const deviceTimezone = getDeviceTimeZone();
    const timeZones = filterNullish(data?.allTimezones ?? []);
    const localTimezoneNode = timeZones.find(tz => tz.properName === localTimezone);
    const proposedTimezoneNode = timeZones.find(tz => tz.properName === deviceTimezone);
    const proposedTimezone = proposedTimezoneNode?.properName;

    useEffect(() => {
        // Determine if we should show the modal
        //  - is time zone valid? (e.g. exists in the backend)
        //  - is device time zone different than user time zone?
        //  - is time zone difference different from what was previously shown?
        if (!proposedTimezone) {
            // no matching timezone found
            return;
        }

        // no diff
        if (localTimezoneNode?.utcOffset === proposedTimezoneNode?.utcOffset) {
            localStorage.removeItem(TIME_ZONE_MODAL_STORAGE_KEY);
            return;
        }

        const storageDataString = localStorage.getItem(TIME_ZONE_MODAL_STORAGE_KEY);
        const storageData = storageDataString ? JSON.parse(storageDataString) : null;
        // no mismatch data found
        if (!storageData) {
            setVisible(true);
            return;
        }

        // not a match to what was last shown
        if (
            storageData.profileTimeZone !== localTimezone ||
            storageData.deviceTimeZone !== proposedTimezone
        ) {
            setVisible(true);
            return;
        }
    }, [
        localTimezone,
        proposedTimezone,
        setVisible,
        localTimezoneNode?.utcOffset,
        proposedTimezoneNode?.utcOffset,
    ]);

    const handleModalClose = async () => {
        localStorage.setItem(
            TIME_ZONE_MODAL_STORAGE_KEY,
            JSON.stringify({
                profileTimeZone: localTimezone,
                deviceTimeZone: proposedTimezone,
            }),
        );
        setVisible(false);
    };

    const updateUserTimeZone = async () => {
        handleModalClose();

        try {
            await dispatch.user.updateMemberProfile({ localTimezone: deviceTimezone });
            toastNotificationsService.renderSuccessToast(t(config.messages.userInfoUpdateSuccess));
        } catch (e) {
            toastNotificationsService.renderErrorToast(t(config.messages.genericError));
        }
    };

    return (
        <Modal
            isVisible={visible}
            title={t('user:time_zone_modal.header')}
            description={t('user:time_zone_modal.description', {
                local_time_zone: `${localTimezoneNode?.commonName} (${localTimezoneNode?.utcOffsetFormatted})`,
                device_time_zone: `${proposedTimezoneNode?.commonName} (${proposedTimezoneNode?.utcOffsetFormatted})`,
            })}
            onCloseButtonClick={handleModalClose}
            data-loaded={!loading}
        >
            <StyledButtonGroup>
                <Button onClick={handleModalClose} variant="secondary" size="medium">
                    {t('user:time_zone_modal.not_now_button.title')}
                </Button>
                <Button data-loaded={!loading} onClick={updateUserTimeZone} size="medium">
                    {t('user:time_zone_modal.update_button.title')}
                </Button>
            </StyledButtonGroup>
        </Modal>
    );
};
